import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Analytics from '../services/analytics';
import TitleHelmet from 'components/TitleHelmet';
import Syringe from '../components/SyringeSVG';
import OtherCalcs from 'components/OtherCalcs';
import 'css/tooltips.css';
// Import our new components
import { CalculatorInput, CalculatorWarning } from 'components/calc';
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage';
import { useQueryParams } from 'components/hooks/useQueryParams';

const ReverseDoseCalc: React.FC = () => {
  // Get both concentration and units from the hook
  const { concentration: queryConcentration, units: queryUnits } = useQueryParams();
  const navigate = useNavigate();

  // Removed manual parsing for queryUnits
  // const queryUnits = new URLSearchParams(window.location.search).get('u');

  // Use the custom storage hook
  const [concentration, setConcentration] = useCalculatorStorage({
    key: 'concentration',
    initialValue: '',
    queryValue: queryConcentration || undefined,
  });

  const [units, setUnits] = useCalculatorStorage({
    key: 'units',
    initialValue: '',
    queryValue: queryUnits || undefined, // Use queryUnits from the hook
  });

  const [calculatedDosage, setCalculatedDosage] = useState<number | null>(null);
  const [showInputs, setShowInputs] = useState<boolean>(true);

  const handleCalculate = (): void => {
    const concentrationValue = parseFloat(concentration);
    const unitsValue = parseFloat(units);

    if (concentrationValue > 0 && unitsValue > 0) {
      const dosage = (unitsValue / 100) * concentrationValue;
      setCalculatedDosage(dosage);
      setShowInputs(false); // Collapse the input area

      // Scroll to top when calculation is submitted for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Add query params to URL
      navigate(`?c=${concentration}&u=${units}`, { replace: true });

      Analytics.trackCalculation('Reverse', {
        concentration: concentrationValue,
        units: unitsValue,
        result: dosage,
      });
    } else {
      alert('Please enter valid values for both concentration and units');
    }
  };

  const handleShowInputs = (): void => {
    setShowInputs(true);
    setCalculatedDosage(null); // Reset the calculation results
  };

  return (
    <div className="App">
      <TitleHelmet
        title="Fat Scientist | How Much Did I Take? - Reverse Calculator"
        description="How much GLP-1 medication did you take? Use this free, simple calculator to verify your dosage based on units injected and concentration."
      />
      <header className="App-header">
        <Link to="/dosage-calculator" className="smaller-text">
          Looking for the standard Dose Calculator?
        </Link>
        <h1>How Much Did I Take?</h1>
        <p>Enter the concentration and the number of units to calculate the dosage in milligrams.</p>
      </header>
      {showInputs ? (
        <div className="calculator-container">
          <div className="input-container">
            <CalculatorInput
              id="concentration"
              value={concentration}
              onChange={setConcentration}
              unit="mg/mL"
              placeholder="e.g., 10"
              label="Concentration (mg/mL)"
            />

            <CalculatorInput
              id="units"
              value={units}
              onChange={setUnits}
              unit="units"
              placeholder="e.g., 50"
              label="Units"
            />
          </div>
          <button onClick={handleCalculate} className="calculate-button">
            Calculate
          </button>
        </div>
      ) : (
        <span>
          &#9664;{' '}
          <Link className="smaller-text" onClick={handleShowInputs} to="">
            Reset calculator and start again
          </Link>
        </span>
      )}
      {calculatedDosage !== null && (
        <div className="result centered">
          <div>
            {parseFloat(concentration) <= 6 && (
              <CalculatorWarning
                message={`Warning: The concentration of ${concentration}mg/mL is unusually low - you may have made an error. Normal concentration for
                tirzepatide is generally 7mg/mL to 30mg/mL. Please double-check your vial label to confirm this concentration is
                correct.`}
              />
            )}
            {parseFloat(concentration) > 30 && (
              <CalculatorWarning
                message={`The concentration of ${concentration}mg/mL is unusually high. Normal concentration for
                      tirzepatide is generally between 7mg/mL and 30mg/mL. Please double-check your vial label to confirm
                      this concentration is correct.`}
              />
            )}
            For {units} units at {concentration} mg/mL concentration,
            <div className="resultsText">
              you took {Number(calculatedDosage.toFixed(2)).toString()} mg of medication.
              <br />
            </div>
            <Syringe calculatedVolume={parseFloat(units) / 100} />
            {calculatedDosage >= 20 ? (
              <CalculatorWarning
                type="urgent"
                message={
                  <>
                    The calculated dose of {Number(calculatedDosage.toFixed(2)).toString()}mg is very high! Contact your
                    healthcare provider IMMEDIATELY.
                  </>
                }
              />
            ) : (
              <CalculatorWarning message="If this is higher than your prescribed dose, you should contact your healthcare provider immediately." />
            )}
          </div>
        </div>
      )}
      <p className="disclaimer">
        Disclaimer: This calculator is for informational purposes only and is not a substitute for professional medical
        advice. Always verify your dose with your healthcare provider. Fat Scientist is not responsible for any
        consequences from using this tool.
      </p>

      <OtherCalcs />
    </div>
  );
};

export default ReverseDoseCalc;
